import { type Region } from "shared/services/appConfig/types";
import { useUserStore } from "shared/stores/user";
import {
  type CurrentAdminOrganisation,
  type Organisation,
} from "shared/stores/user/types";
import { type Nullable } from "shared/types";

export interface UserLocation {
  continent: string;
  region: Uppercase<Exclude<Region, "uk">> | "GB";
}

export interface CurrentUserLocation {
  region: Region;
  timezone: string;
}

function isCurrentOrganisation(
  organisation: Nullable<Organisation | CurrentAdminOrganisation>
): organisation is CurrentAdminOrganisation {
  return organisation !== null && "time_zone" in organisation;
}

export function useUserLocation(
  organisation: Nullable<Organisation | CurrentAdminOrganisation> = null
) {
  function getCurrentUserLocation(): CurrentUserLocation {
    const userStore = useUserStore();
    const region = userStore.currentUser.organisation.region || "au";
    const timezone = userStore.currentUser.time_zone;

    return {
      region,
      timezone,
    };
  }

  function getUserLocation(): UserLocation {
    const location = isCurrentOrganisation(organisation)
      ? { region: organisation.region, timezone: organisation.time_zone }
      : getCurrentUserLocation();

    const { timezone } = location;
    let { region }: { region: Region | string } = location;

    const [continent] = timezone?.split("/") || [];

    if (region === "uk") {
      region = "gb";
    }

    return <UserLocation>{
      continent,
      region: region.toUpperCase(),
    };
  }

  return {
    getUserLocation,
  };
}
